import { useEffect, useState } from "react";

/**
 * Recharts dibuja con valores concretos (atributos SVG), no con `var(--x)`:
 * los custom properties hay que resolverlos antes de pasárselos. Este módulo
 * lee la paleta del tema desde CSS —única fuente de verdad— y la vuelve a leer
 * cuando cambia el esquema de color o el `data-theme` del root.
 */

export const CHART_SERIES_SLOTS = 8;

export interface ChartTokens {
  /** Los ocho slots categóricos, en orden fijo. Nunca se ciclan. */
  series: string[];
  surface: string;
  grid: string;
  axis: string;
  label: string;
  ink: string;
  inkSecondary: string;
  tooltipSurface: string;
  focused: string;
  good: string;
  warning: string;
  critical: string;
  neutral: string;
}

const FALLBACK: ChartTokens = {
  series: ["#2a78d6", "#eb6834", "#1baf7a", "#eda100", "#e87ba4", "#008300", "#4a3aa7", "#e34948"],
  surface: "#ffffff",
  grid: "#e6e6e6",
  axis: "#c9c9c9",
  label: "#8a8a8a",
  ink: "#0b0b0b",
  inkSecondary: "#4d4d4d",
  tooltipSurface: "#ffffff",
  focused: "#0b0b0b",
  good: "#0ca30c",
  warning: "#fab219",
  critical: "#d03b3b",
  neutral: "#8a8a8a",
};

function readVar(scope: CSSStyleDeclaration, name: string, fallback: string): string {
  const value = scope.getPropertyValue(name).trim();
  return value.length > 0 ? value : fallback;
}

function readTokens(): ChartTokens {
  if (typeof window === "undefined" || typeof document === "undefined") return FALLBACK;
  const scope = getComputedStyle(document.documentElement);
  return {
    series: Array.from({ length: CHART_SERIES_SLOTS }, (_, index) =>
      readVar(scope, `--chart-${index + 1}`, FALLBACK.series[index] ?? FALLBACK.series[0]),
    ),
    surface: readVar(scope, "--chart-surface", FALLBACK.surface),
    grid: readVar(scope, "--chart-grid", FALLBACK.grid),
    axis: readVar(scope, "--chart-axis", FALLBACK.axis),
    label: readVar(scope, "--chart-label", FALLBACK.label),
    ink: readVar(scope, "--chart-ink", FALLBACK.ink),
    inkSecondary: readVar(scope, "--chart-ink-secondary", FALLBACK.inkSecondary),
    tooltipSurface: readVar(scope, "--chart-tooltip-surface", FALLBACK.tooltipSurface),
    focused: readVar(scope, "--chart-focused", FALLBACK.focused),
    good: readVar(scope, "--status-good", FALLBACK.good),
    warning: readVar(scope, "--status-warning", FALLBACK.warning),
    critical: readVar(scope, "--status-critical", FALLBACK.critical),
    neutral: readVar(scope, "--status-neutral", FALLBACK.neutral),
  };
}

/** Devuelve la paleta de gráficos resuelta y reacciona a los cambios de tema. */
export function useChartTokens(): ChartTokens {
  const [tokens, setTokens] = useState<ChartTokens>(() => readTokens());

  useEffect(() => {
    const refresh = () => setTokens(readTokens());
    refresh();

    const media = window.matchMedia("(prefers-color-scheme: dark)");
    media.addEventListener("change", refresh);

    const observer = new MutationObserver(refresh);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme", "style", "class"],
    });

    return () => {
      media.removeEventListener("change", refresh);
      observer.disconnect();
    };
  }, []);

  return tokens;
}

/**
 * Color de una serie: respeta `color_token` si el backend nombra un token del
 * tema (`accent`, `chart-3`, `status-warning`…); si no, cae al slot categórico
 * que le toca por posición. El orden de los slots es fijo y nunca se cicla:
 * a partir del noveno la serie se queda con el último color y la leyenda la
 * sigue identificando por nombre.
 */
export function seriesColor(
  tokens: ChartTokens,
  index: number,
  colorToken?: string | null,
): string {
  if (colorToken) {
    const normalized = colorToken.trim().toLowerCase();
    const slotMatch = /^(?:chart[-_]?)([1-8])$/.exec(normalized);
    if (slotMatch?.[1]) return tokens.series[Number(slotMatch[1]) - 1] ?? tokens.series[0];
    const named: Record<string, string> = {
      accent: tokens.series[0] ?? FALLBACK.series[0],
      blue: tokens.series[0] ?? FALLBACK.series[0],
      orange: tokens.series[1] ?? FALLBACK.series[1],
      aqua: tokens.series[2] ?? FALLBACK.series[2],
      yellow: tokens.series[3] ?? FALLBACK.series[3],
      magenta: tokens.series[4] ?? FALLBACK.series[4],
      green: tokens.series[5] ?? FALLBACK.series[5],
      violet: tokens.series[6] ?? FALLBACK.series[6],
      red: tokens.series[7] ?? FALLBACK.series[7],
      good: tokens.good,
      warning: tokens.warning,
      critical: tokens.critical,
      neutral: tokens.neutral,
    };
    const resolved = named[normalized];
    if (resolved) return resolved;
  }
  const slot = tokens.series[index % tokens.series.length];
  return slot ?? FALLBACK.series[0];
}

/** Convierte un color a `rgb(... / alpha)` para los lavados de área. */
export function withAlpha(color: string, alpha: number): string {
  const hex = color.trim().replace("#", "");
  if (!/^[0-9a-f]{6}$/i.test(hex)) return color;
  const r = Number.parseInt(hex.slice(0, 2), 16);
  const g = Number.parseInt(hex.slice(2, 4), 16);
  const b = Number.parseInt(hex.slice(4, 6), 16);
  return `rgb(${r} ${g} ${b} / ${alpha})`;
}
